import { useEffect, useState } from 'react';
import type { CountdownBlock as CountdownBlockDoc } from '../types';

/**
 * Odpočet. `fixed` cílí na pevný `endsAt`; `evergreen` běží od prvního
 * zobrazení (konec drží store, takže reload odpočet neresetuje) — jeho konec
 * dostane blok zvenčí přes `evergreenEndsAt`.
 *
 * Po vypršení buď skryje celou vrstvu (`hide_layer` → `onExpireHide`), nebo
 * ukáže náhradní text (`show_message`).
 */
export function CountdownBlock({
  block,
  evergreenEndsAt,
  onExpireHide,
}: {
  block: CountdownBlockDoc;
  evergreenEndsAt: string | null;
  onExpireHide: () => void;
}) {
  const target = resolveTarget(block, evergreenEndsAt);
  const [remaining, setRemaining] = useState(() => remainingMs(target));

  useEffect(() => {
    if (target === null) return undefined;
    const id = window.setInterval(() => setRemaining(remainingMs(target)), 1000);
    return () => window.clearInterval(id);
  }, [target]);

  const expired = target === null || remaining <= 0;

  useEffect(() => {
    if (expired && block.expired === 'hide_layer') onExpireHide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired]);

  if (expired) {
    if (block.expired === 'hide_layer') return null;
    return <p className="layer-countdown is-expired">{block.expiredText ?? ''}</p>;
  }

  return (
    <div className="layer-countdown" role="timer" aria-live="off">
      {formatParts(remaining).map((part) => (
        <span key={part.label} className="layer-countdown-part">
          <strong>{part.value}</strong>
          <span className="layer-countdown-unit">{part.label}</span>
        </span>
      ))}
    </div>
  );
}

function resolveTarget(block: CountdownBlockDoc, evergreen: string | null): number | null {
  if (block.mode === 'fixed') {
    if (block.endsAt === undefined) return null;
    const time = new Date(block.endsAt).getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (evergreen === null) return null;
  const time = new Date(evergreen).getTime();
  return Number.isFinite(time) ? time : null;
}

function remainingMs(target: number | null): number {
  if (target === null) return 0;
  return Math.max(0, target - Date.now());
}

function formatParts(ms: number): readonly { label: string; value: string }[] {
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  const parts: { label: string; value: string }[] = [];
  if (days > 0) parts.push({ label: 'dní', value: String(days) });
  parts.push({ label: 'h', value: pad(hours) });
  parts.push({ label: 'm', value: pad(minutes) });
  parts.push({ label: 's', value: pad(seconds) });
  return parts;
}
